import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useLogout } from "src/hooks/useLogout";
import { ProfileContext } from "src/providers/profile-provider";
import { useGetBusiness } from "src/hooks/useGetBusiness";

import Layout from "src/components/Layout/Layout";
import Notification from "src/components/Notification/Notification";
import { useBusinessJoinRequest } from "src/hooks/useBusinessJoinRequest";

export type Context = {
  businessId?: string;
  pendingRequests: Partial<Omit<ReturnType<typeof useBusinessJoinRequest>, "loading" | "error">>;
} & ReturnType<typeof useGetBusiness>;

export const BusinessContext = createContext<Partial<Context> & { pendingRequests: Context["pendingRequests"] }>({
  pendingRequests: {},
});

const BusinessBase: React.FC<PropsWithChildren> = ({ children }) => {
  const logout = useLogout();
  const { profile } = useContext(ProfileContext);
  const { businessId } = useParams<{ businessId?: string }>();

  const { business, loading, reload, error } = useGetBusiness(businessId);
  const {
    loading: loadingRequests,
    error: errorLoadingRequests,
    ...pendingRequests
  } = useBusinessJoinRequest(businessId, "pending");

  const context = useMemo(
    () => ({
      businessId,
      business,
      loading: loadingRequests || loading,
      pendingRequests,
      reload,
    }),
    [businessId, business, loading, loadingRequests, pendingRequests, reload],
  );

  return (
    <BusinessContext.Provider value={context}>
      <Layout
        className="bg-secondary"
        logout={logout}
        profile={profile}
        business={business}
        businessId={businessId}
        businessRequestCount={pendingRequests.count}
      >
        {(error || errorLoadingRequests) && (
          <Notification
            className="mb-5"
            level="error"
            message={error?.message || errorLoadingRequests?.message}
            type="closable"
          />
        )}
        {children}
      </Layout>
    </BusinessContext.Provider>
  );
};

export default BusinessBase;
